import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../utils/supabaseClient'
import { useTheme } from '../components/ThemeProvider'

const Contact = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [name, setName] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!message.trim()) {
      setError('Please share your message with us')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Try to save to Supabase first
      const { error: supabaseError } = await supabase
        .from('feedback')
        .insert([
          {
            name: name.trim() || null,
            message: message.trim(),
            timestamp: new Date().toISOString()
          }
        ])

      if (supabaseError) {
        // Fallback to localStorage if Supabase fails
        const feedback = {
          id: crypto.randomUUID(),
          name: name.trim() || 'Anonymous',
          message: message.trim(),
          timestamp: new Date().toISOString()
        }
        
        const existingFeedback = JSON.parse(localStorage.getItem('feedback') || '[]')
        existingFeedback.unshift(feedback)
        localStorage.setItem('feedback', JSON.stringify(existingFeedback))
      }

      setShowConfirmation(true)
      setName('')
      setMessage('')
      
      // Auto-hide confirmation after 3 seconds
      setTimeout(() => {
        setShowConfirmation(false)
      }, 3000)
      
    } catch (err) {
      console.error('Error submitting feedback:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-white/60 dark:bg-sage-800/60 border-b border-sage-100/30 dark:border-sage-700/30">
        <button
          onClick={() => navigate('/home')}
          className="p-3 rounded-2xl hover:bg-sage-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
        >
          <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-light text-sage-600 dark:text-sage-300">Contact Us</h1>
        <div className="w-11"></div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm">
          <div className="text-center mb-8">
            <div className="w-12 h-12 mx-auto mb-6 bg-peach-100/60 dark:bg-peach-900/30 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-peach-400 dark:text-peach-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-peach-600 dark:text-peach-300 font-light text-lg sm:text-xl mb-4">We'd Love to Hear From You</h2>
            <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
              Share your thoughts, suggestions, or just say hello. Every message helps us create a better experience.
            </p>
          </div>

          {/* Confirmation Message */}
          {showConfirmation && (
            <div className="mb-6 p-4 bg-mint-50/80 dark:bg-mint-900/30 rounded-2xl border border-mint-100/50 dark:border-mint-700/30 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <svg className="w-4 h-4 text-mint-500 dark:text-mint-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-mint-600 dark:text-mint-300 font-light text-sm">
                  Thank you for sharing. We listen gently.
                </p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sage-600 dark:text-sage-300 font-light text-xs sm:text-sm mb-2">
                Name (optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How would you like us to address you?"
                className="w-full p-3 sm:p-4 rounded-2xl border border-sage-200/50 dark:border-sage-600/50 focus:border-sage-300 dark:focus:border-sage-500 focus:ring-2 focus:ring-sage-100 dark:focus:ring-sage-600 focus:outline-none font-light text-xs sm:text-sm bg-white/50 dark:bg-sage-900/50 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sage-600 dark:text-sage-300 font-light text-xs sm:text-sm mb-2">
                Your message *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Share your thoughts, feedback, or questions. We read every message with care."
                className="w-full p-3 sm:p-4 rounded-2xl border border-sage-200/50 dark:border-sage-600/50 focus:border-sage-300 dark:focus:border-sage-500 focus:ring-2 focus:ring-sage-100 dark:focus:ring-sage-600 focus:outline-none font-light text-xs sm:text-sm bg-white/50 dark:bg-sage-900/50 dark:text-white resize-none"
                rows={6}
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-peach-50/60 dark:bg-peach-900/30 border border-peach-200/50 dark:border-peach-700/30 rounded-2xl">
                <p className="text-peach-600 dark:text-peach-300 text-xs sm:text-sm font-light">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !message.trim()}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-sage-100/80 hover:bg-sage-200/60 
                         dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                         text-sage-600 dark:text-sage-300 rounded-2xl text-sm sm:text-base font-light 
                         border border-sage-200/50 dark:border-sage-600/40 
                         hover:border-sage-300/60 dark:hover:border-sage-500/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-sage-100 dark:focus:ring-sage-600/30
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending gently...' : 'Send Message'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sage-400 dark:text-sage-500 text-xs font-light leading-relaxed">
              We typically respond within a few days. Thank you for your patience.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact